import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useRecorder } from 'react-microphone-recorder';
// import useRecorder from 'util/UseRecorder';
// @TODO: Remove speech-to-text recognition
// import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import getBlobDuration from 'get-blob-duration';
// import { registerMicrophoneStream } from "util/microphonePermissionManager";

/**
 * ReusableRecorder Component
 * 
 * Purpose: Provides a reusable audio recording solution compatible with React 19
 * Replaces: Deprecated mic-recorder-to-mp3 library
 * 
 * Features:
 * - Records audio using react-microphone-recorder hook
 * - Integrates speech-to-text recognition
 * - Auto-stops at max duration
 * - Generates blob URL and file for upload
 * - Exposes imperative API via ref
 */

const getBlobURL = (blob, file) => {
  let url = null;
  try {
    if (blob && blob.size > 0) {
      const objectToUse = file || blob;
      url = URL.createObjectURL(objectToUse);
    }
  } catch (err) {
    console.error("Error creating blob URL:", err);
  }
  return url;
};
 
const getFile = (blob, fileName) => {
  let file = null;
  try {
    const audioBlob = new Blob([blob], { type: "audio/mp3" });
    file = new File([audioBlob], fileName, {
      type: "audio/mp3",
      lastModified: Date.now(),
    });
  } catch (err) {
    file = new File([], fileName, {
      type: "audio/mp3",
      lastModified: Date.now(),
    });
  }
  return file;
};
 
const getDuration = async (blob) => {
  let duration = 0;
  duration = await getBlobDuration(blob)
    .then((d) => {
      return d;
    })
    .catch(async (e) => {
      console.log(e);
      return 0;
    });
  return duration;
};

const ReusableRecorder = forwardRef(({ 
  onRecordingComplete, 
  onRecordingStart, 
  onRecordingStop,
  maxDuration = 40000, // Default 40 seconds
  enableSpeechRecognition = false,
  autoStart = false,
  disabled = false,
  fileName = 'noname.mp3',
  // stream,
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const blobURLRef = useRef('');
  // const streamRef = useRef(null);
  const timerRef = useRef(null);
  // const transcriptRef = useRef('');

  // Hook from react-microphone-recorder for audio recording
  const { startRecording: startMicRecording, stopRecording: stopMicRecording , audioBlob } = useRecorder();
  
  // Hook from react-speech-recognition for speech-to-text
  // const { transcript, resetTranscript } = useSpeechRecognition();

  // console.log("recordi audioBlob,audioFile,audioURLngBlob", audioBlob,audioFile,audioURL);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
    isRecording,
    revokeBlobURL
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Auto-start recording if enabled
  useEffect(() => {
    if (autoStart && !isRecording) {
      // console.log('🎙️ [ReusableRecorder] Auto-starting recording');
      startRecording();
    }
  }, [autoStart]);

  // Process recording when blob is available
  // useEffect(() => {
  //   if (audioBlob) {
  //     // console.log('🎙️ [ReusableRecorder] Recording blob received, processing...');
  //     processRecording(audioBlob);
  //   }
  // }, [audioBlob]);
  useEffect(() => {
    if (audioBlob && !isRecording) {
      processRecording(audioBlob);
    }
  }, [audioBlob, isRecording]);

  /**
   * Start Recording
   * - Initializes microphone recording
   * - Starts speech recognition if enabled
   * - Sets up timer to auto-stop at maxDuration
   */
  const startRecording = async () => {
    if (disabled) {
      console.log('⚠️ [ReusableRecorder] Recording disabled');
      return;
    }

    try {
      // console.log('▶️ [ReusableRecorder] START RECORDING');
      // console.log('⏱️ [ReusableRecorder] Max duration:', maxDuration / 1000, 'seconds');
      
      setIsRecording(true);
      setRecordingTime(0);
      // transcriptRef.current = '';

      // Start speech recognition if enabled
      if (enableSpeechRecognition) {
        // console.log('🗣️ [ReusableRecorder] Starting speech recognition (en-GB)');
        // resetTranscript();
        // SpeechRecognition.startListening({ continuous: true });
      }

      // Start microphone recording using useRecorder hook
      // console.log('🎤 [ReusableRecorder] Calling startMicRecording() from useRecorder hook');
      startMicRecording();
    // Register the actual stream from useRecorder
      // if(stream){
      //   stream.current.getTracks().forEach(track => track.stop());
      //   registerMicrophoneStream(stream);
      //   console.log('🎤 [ReusableRecorder] Microphone stream registered');
      // } else {
      //   console.warn('⚠️ [ReusableRecorder] No stream available from useRecorder');
      // }

      // Setup timer to track duration and auto-stop
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 100;
          if (newTime >= maxDuration) {
            // console.log('⏰ [ReusableRecorder] Max duration reached, auto-stopping');
            stopRecording();
          }
          return newTime;
        });
      }, 100);

      if (onRecordingStart) {
        onRecordingStart();
      }
      
      console.log('✅ [ReusableRecorder] Recording started successfully');
    } catch (error) {
      console.error('❌ [ReusableRecorder] Error starting recording:', error);
      setIsRecording(false);
    }
  };

  /**
   * Stop Recording
   * - Stops microphone recording
   * - Stops speech recognition
   * - Captures final transcript
   * - Triggers processing of recorded audio
   */
  const stopRecording = () => {
    if (!isRecording) {
      console.log('⚠️ [ReusableRecorder] Not recording, cannot stop');
      return;
    }

    // console.log('⏹️ [ReusableRecorder] STOP RECORDING');
    // console.log('⏱️ [ReusableRecorder] Recording duration:', (recordingTime / 1000).toFixed(2), 'seconds');

    // Clear duration timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      revokeBlobURL();
    }

    // Capture transcript before stopping speech recognition
    if (enableSpeechRecognition) {
      // transcriptRef.current = transcript;
      // console.log('🗣️ [ReusableRecorder] Captured transcript:', transcript || '(empty)');
      // SpeechRecognition.stopListening();
    }

    // Stop microphone recording using useRecorder hook
    // console.log('🎤 [ReusableRecorder] Calling stopMicRecording() from useRecorder hook');
    stopMicRecording();
    setIsRecording(false);

    if (onRecordingStop) {
      onRecordingStop();
    }
    
    // console.log('✅ [ReusableRecorder] Recording stopped successfully');
  };

  /**
   * Process Recording
   * - Converts blob to URL for playback
   * - Calculates audio duration
   * - Creates File object for upload
   * - Calls onRecordingComplete callback with all data
   */
  const processRecording = async (blob) => {
    try {
      // console.log('🔄 [ReusableRecorder] Processing recording blob');
      // console.log('📦 [ReusableRecorder] Blob size:', (blob.size / 1024).toFixed(2), 'KB');
      
      // const blobURL = URL.createObjectURL(blob);
      // const duration = await getBlobDuration(blobURL);
      // const file = new File([blob], fileName, { type: 'audio/mp3' });
       // Generate file and URL
      const file = getFile(blob, fileName);  // fileName comes from props
      if (!file) throw new Error("Failed to create file");

      const blobURL = getBlobURL(blob, file);
      if (!blobURL) throw new Error("Failed to create blob URL");

      blobURLRef.current = blobURL; // Store for later revocation

      // Get duration (async)
      const duration = await getDuration(blob);
      console.log('⏱️ [ReusableRecorder] Audio duration:', duration.toFixed(2), 'seconds');
      console.log('📄 [ReusableRecorder] File created:', file.name, file.type);
      console.log('🔗 [ReusableRecorder] Blob URL:', blobURL);
      // console.log("[ReusableRecorder] transcript:",transcript);

      if (onRecordingComplete) {
        // console.log('📤 [ReusableRecorder] Calling onRecordingComplete callback');
        onRecordingComplete({
          blob,
          url: blobURL,
          transcript: '', // Speech recognition disabled
          duration,
          file
        });
      }
      
      // console.log('✅ [ReusableRecorder] Recording processed successfully');
    } catch (error) {
      console.error('❌ [ReusableRecorder] Error processing recording:', error);
    }
  };

  /**
   * Cleanup
   * - Clears timers
   * - Stops speech recognition
   * - Called on component unmount
   */
  const cleanup = () => {
    // console.log('🧹 [ReusableRecorder] Cleaning up');
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (enableSpeechRecognition) {
      // SpeechRecognition.stopListening();
    }
    revokeBlobURL();
  };

  const revokeBlobURL = () => {
    if (blobURLRef.current && blobURLRef.current.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(blobURLRef.current);
        console.log('🧹 Revoked blob URL:', blobURLRef.current);
      } catch (err) {
        console.error('⚠️ Failed to revoke blob URL:', err);
      } finally {
        blobURLRef.current = '';
      }
    }
  };

  // Component renders nothing (invisible)
  return null;
});

ReusableRecorder.displayName = 'ReusableRecorder';

export default ReusableRecorder;
 